import axios from "axios";
import { useCallback, useEffect, useState } from "react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Same-origin API: the live site is served on several hostnames that all proxy /api.
export const api = axios.create({ baseURL: "/api", withCredentials: true });

let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const url = cfg.url || "";
    const retryable = cfg.method === "get" || url.includes("/auth/login");
    if (!error.response && retryable && !cfg._netRetry) {
      cfg._netRetry = true;
      await new Promise((r) => setTimeout(r, 1500));
      return api(cfg);
    }
    if (error.response?.status === 401 && !cfg._retry && !url.includes("/auth/refresh") && !url.includes("/auth/login")) {
      cfg._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(cfg);
      } catch (e) {
        refreshing = null;
      }
    }
    return Promise.reject(error);
  }
);

export function errMsg(e) {
  if (e && !e.response && e.request) return "We couldn't reach Tri County Allstars right now. Please check your connection and try again in a moment.";
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x))).join(" ");
  if (typeof d.msg === "string") return d.msg;
  return String(d);
}

export function fileUrl(u) {
  return u || "";
}

export async function uploadFile(file, path = "/manage-upload") {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.url;
}

export function videoEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.(mp4|mov|webm)(\?|$)/i.test(url)) return { type: "video", src: fileUrl(url) };
  return null;
}

export function isVideo(url) {
  return !!videoEmbed(url);
}

export function fmtDate(d, opts = { month: "short", day: "numeric", year: "numeric" }) {
  if (!d) return "Date TBA";
  const dt = new Date(d.length <= 10 ? `${d}T12:00:00` : d);
  return isNaN(dt) ? d : dt.toLocaleDateString("en-US", opts);
}

export function useFetch(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    try {
      const r = await api.get(path);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [path]);
  useEffect(() => {
    load();
  }, [load]);
  return { data, loading, error, reload: load };
}

export function usePageTitle(title) {
  useEffect(() => {
    document.title = title ? `${title} | Tri County Allstars` : "Tri County Allstars | Different Parks. One Coast. One Mission.";
  }, [title]);
}
